import { maxBy, uniq } from 'es-toolkit';
import { createSelector } from '@reduxjs/toolkit';
import paper from 'paper';

import { Annotation } from '@paragon/contract-library';
import { notNullish } from '@paragon/utilities';

import { RootState } from '../store';
import { User, UserData } from '../store/users';
import { getSelectedPage } from './documents';

const getAnnotations = (state: RootState) => state.annotations.annotationData;
const getUsers = (state: RootState) => state.users;
const getLogInData = (state: RootState) => state.logInData;

const getSelectedGuids = (state: RootState) => state.annotations.selectedGuids;

export const getAnnotationsForCurrentPage = createSelector(
  [getSelectedPage, getAnnotations],
  (selectedPage, allAnnotations) => {
    if (selectedPage == null || allAnnotations[selectedPage.id] == null) return [];
    return allAnnotations[selectedPage.id];
  },
);

export const getAnnotationsForCurrentPageWithSelectionData = createSelector(
  [getAnnotationsForCurrentPage, getSelectedGuids],
  (annotations, selectedGuids) =>
    annotations.map((annotation) => ({
      ...annotation,
      selected: selectedGuids.includes(annotation.guid),
    })),
);

export const getSelectedAnnotationsForCurrentPage = createSelector(
  [getAnnotationsForCurrentPage, getSelectedGuids],
  (annotationsForSelectedPage, selectedGuids) =>
    annotationsForSelectedPage.filter((annotation) => selectedGuids.includes(annotation.guid)),
);

export const getNonSystemSelectedAnnotationGuidsForCurrentPage = createSelector(
  [getSelectedAnnotationsForCurrentPage],
  (selectedAnnotationsForSelectedPage) =>
    selectedAnnotationsForSelectedPage
      .filter((annotation) => annotation.category !== 'system')
      .map((annotation) => annotation.guid),
);

export const getNextZIndexForCurrentPage = createSelector([getAnnotationsForCurrentPage], (currentPageAnnotations) => {
  if (currentPageAnnotations == null || currentPageAnnotations.length === 0) return 0;
  return maxBy(currentPageAnnotations, (annotation) => annotation.zIndex)!.zIndex + 1;
});

export const getUsersWithAnnotationsOnCurrentPage = createSelector(
  [getAnnotationsForCurrentPage, getUsers],
  (currentPageAnnotations, users) => {
    if (currentPageAnnotations == null || currentPageAnnotations.length === 0) return [];

    const currentPageAnnotationsWithoutSystemAnnotations = currentPageAnnotations.filter(
      (annotation) => annotation.category !== 'system',
    );

    const emailsWithAnnotationsOnCurrentPage = uniq(
      currentPageAnnotationsWithoutSystemAnnotations.map((annotation) => annotation.creatorEmail).filter(notNullish),
    );

    return emailsWithAnnotationsOnCurrentPage.reduce<User[]>((accumulator, email) => {
      if (users.userData[email] != null) accumulator.push(users.userData[email]);
      return accumulator;
    }, []);
  },
);

export const getUsersWithAnnotationsOnCurrentPageExceptCurrentUser = createSelector(
  [getUsersWithAnnotationsOnCurrentPage, getLogInData],
  (usersWithAnnotations, logInData) => {
    if (usersWithAnnotations == null || usersWithAnnotations.length === 0) {
      return [];
    }

    return usersWithAnnotations.filter((user) => user.email !== logInData.loggedInUser!.email);
  },
);

const areSystemAnnotationsEditable = (state: RootState) => state.annotations.systemAnnotationsEditable;

export function annotationIsEditableByUser(
  annotation: Annotation,
  user: User,
  otherUsers: UserData,
  systemAnnotationsEditable = false,
) {
  if (annotation.category === 'system') {
    return systemAnnotationsEditable;
  }

  if (annotation.creatorEmail == null || annotation.creatorEmail === user.email) {
    return true;
  }

  const annotationCreator = otherUsers[annotation.creatorEmail];
  const annotationCreatedWithinSameOrganization =
    annotationCreator != null && user != null && annotationCreator.organization === user.organization;

  return annotationCreatedWithinSameOrganization && annotation.sharedWith === 'organization';
}

export const getEditableAnnotationsOnActivePage = createSelector(
  [getAnnotationsForCurrentPage, getLogInData, getUsers, areSystemAnnotationsEditable],
  (annotations, { loggedInUser }, { userData }, systemAnnotationsEditable) =>
    annotations.filter((annotation) =>
      annotationIsEditableByUser(annotation, userData[loggedInUser!.email], userData, systemAnnotationsEditable),
    ),
);

export function annotationIntersects(annotation: Annotation, rectangle: paper.Rectangle) {
  const annotationBounds = new paper.Rectangle(annotation.boundingBox.topLeft, annotation.boundingBox.bottomRight);

  return rectangle.intersects(annotationBounds);
}
